import type { MedplumClient } from "@medplum/core";
import type { CodeableConcept, Device as FHIRDevice, Identifier } from "@medplum/fhirtypes";
import type { CreateResourceOptions } from "../fhir/FhirResource";
import { FhirResource } from "../fhir/FhirResource";
import {
  getProviderSlug,
  type JunctionSource,
  WEARABLE_IDENTIFIER_SYSTEM_JUNCTION_USER,
  WEARABLE_IDENTIFIER_SYSTEM_PROVIDER,
} from "./types";

const PROVIDER_SLUG_EXTENSION = "vibrant.wearable.provider-slug";
const CONNECTION_STATUS_EXTENSION = "vibrant.wearable.connection-status";

export type WearableConnectionStatus = "connected" | "disconnected" | "pending";

/**
 * Data structure for creating or updating a WearableDevice.
 */
export interface WearableDeviceData {
  providerSlug: string;
  patientId: string;
  junctionUserId: string;
  providerName?: string;
  providerLogo?: string;
  hardwareVersion?: string;
  softwareVersion?: string;
  status?: "active" | "inactive";
}

/**
 * Data structure for updating a WearableDevice.
 */
export interface WearableDeviceUpdateData {
  junctionUserId?: string;
  providerName?: string;
  providerLogo?: string;
  hardwareVersion?: string;
  softwareVersion?: string;
  status?: "active" | "inactive";
}

export interface WearableDeviceUpdateParams {
  id: string;
  data: WearableDeviceUpdateData;
  existingDevice?: WearableDevice;
}

/**
 * Query parameters for searching WearableDevices.
 */
export interface WearableDeviceQuery {
  providerSlug?: string;
  junctionUserId?: string;
  patientId?: string;
  status?: "active" | "inactive";
}

/**
 * WearableDevice models a connected wearable provider for a member.
 * It serves as the canonical shared writable Device model for wearable integrations.
 */
export class WearableDevice extends FhirResource<FHIRDevice> {
  static readonly ResourceType = "Device";

  static readonly Identifier = {
    Provider: WEARABLE_IDENTIFIER_SYSTEM_PROVIDER,
    JunctionUser: WEARABLE_IDENTIFIER_SYSTEM_JUNCTION_USER,
  } as const;

  static readonly Status = {
    Active: "active",
    Inactive: "inactive",
  } as const;

  static readonly DeviceType: CodeableConcept = {
    coding: [
      {
        system: "http://snomed.info/sct",
        code: "706689003",
        display: "Personal health monitoring device",
      },
    ],
    text: "Wearable Device",
  };

  //-----------------------------------------------------------------------------------------------
  //                                          GETTERS
  //-----------------------------------------------------------------------------------------------

  get providerSlug(): string {
    return (
      this.resource.identifier?.find(
        (identifier) => identifier.system === WearableDevice.Identifier.Provider,
      )?.value ||
      this.getExtension(PROVIDER_SLUG_EXTENSION)?.valueString ||
      ""
    );
  }

  get junctionUserId(): string {
    return (
      this.resource.identifier?.find(
        (identifier) => identifier.system === WearableDevice.Identifier.JunctionUser,
      )?.value || ""
    );
  }

  get patientId(): string {
    return this.resource.patient?.reference?.split("/")[1] || "";
  }

  get isActive(): boolean {
    return this.resource.status === WearableDevice.Status.Active;
  }

  get connectionStatus(): WearableConnectionStatus {
    const extensionValue = this.getExtension(CONNECTION_STATUS_EXTENSION)?.valueString;
    if (
      extensionValue === "connected" ||
      extensionValue === "disconnected" ||
      extensionValue === "pending"
    ) {
      return extensionValue;
    }

    return this.isActive ? "connected" : "disconnected";
  }

  get isConnected(): boolean {
    return this.connectionStatus === "connected";
  }

  get name(): string | undefined {
    const primaryName = this.resource.deviceName?.find(
      (deviceName) => deviceName.type === "user-friendly-name",
    );
    if (primaryName?.name) {
      return primaryName.name;
    }

    const fallbackName = this.resource.deviceName?.[0];
    if (fallbackName?.name) {
      return fallbackName.name;
    }

    if (this.resource.manufacturer && this.resource.modelNumber) {
      return `${this.resource.manufacturer} ${this.resource.modelNumber}`;
    }

    return this.resource.manufacturer ?? this.resource.modelNumber ?? undefined;
  }

  get providerName(): string {
    return this.resource.deviceName?.[0]?.name || this.providerSlug;
  }

  get manufacturer(): string | undefined {
    return this.resource.manufacturer ?? undefined;
  }

  get modelNumber(): string | undefined {
    return this.resource.modelNumber ?? undefined;
  }

  get providerLogo(): string | undefined {
    const metadata = parseDeviceMetadata(this.resource.note?.[0]?.text);
    return metadata.logo as string | undefined;
  }

  get metadata(): Record<string, unknown> {
    return parseDeviceMetadata(this.resource.note?.[0]?.text);
  }

  get hardwareVersion(): string | undefined {
    return this.metadata.hardwareVersion as string | undefined;
  }

  get softwareVersion(): string | undefined {
    return this.metadata.softwareVersion as string | undefined;
  }

  //-----------------------------------------------------------------------------------------------
  //                                     PURE CONVERTERS
  //-----------------------------------------------------------------------------------------------

  static toFhirQuery(query: WearableDeviceQuery) {
    return {
      identifier: buildIdentifierQuery(query),
      patient: query.patientId ? `Patient/${query.patientId}` : undefined,
      type: "http://snomed.info/sct|706689003",
      status: query.status,
    };
  }

  private static toFhirCreateObject(
    data: WearableDeviceData,
  ): Omit<FHIRDevice, "id" | "resourceType"> {
    const identifiers: Identifier[] = [
      {
        system: WearableDevice.Identifier.Provider,
        value: data.providerSlug,
      },
      {
        system: WearableDevice.Identifier.JunctionUser,
        value: data.junctionUserId,
      },
    ];

    const metadata: Record<string, unknown> = {};
    if (data.providerLogo) {
      metadata.logo = data.providerLogo;
    }
    if (data.hardwareVersion) {
      metadata.hardwareVersion = data.hardwareVersion;
    }
    if (data.softwareVersion) {
      metadata.softwareVersion = data.softwareVersion;
    }
    metadata.connectedAt = new Date().toISOString();

    return {
      identifier: identifiers,
      status: data.status ?? WearableDevice.Status.Active,
      type: WearableDevice.DeviceType,
      patient: {
        reference: `Patient/${data.patientId}`,
      },
      deviceName: data.providerName
        ? [
            {
              name: data.providerName,
              type: "user-friendly-name",
            },
          ]
        : undefined,
      version: data.softwareVersion
        ? [
            {
              value: data.softwareVersion,
            },
          ]
        : undefined,
      note:
        Object.keys(metadata).length > 0
          ? [
              {
                text: JSON.stringify(metadata),
              },
            ]
          : undefined,
    };
  }

  private static toFhirUpdateObject(
    existingDevice: WearableDevice,
    data: WearableDeviceUpdateData,
  ): Record<string, unknown> {
    const patch: Record<string, unknown> = {};

    if (data.status !== undefined) {
      patch.status = data.status;
      patch.extension = [{ url: CONNECTION_STATUS_EXTENSION, valueString: null }];
    }

    if (data.providerName) {
      patch.deviceName = [
        {
          name: data.providerName,
          type: "user-friendly-name",
        },
      ];
    }

    if (data.softwareVersion) {
      patch.version = [{ value: data.softwareVersion }];
    }

    if (data.junctionUserId && data.junctionUserId !== existingDevice.junctionUserId) {
      const existingIdentifiers = existingDevice.serialize().identifier || [];
      patch.identifier = existingIdentifiers
        .filter((identifier) => identifier.system !== WearableDevice.Identifier.JunctionUser)
        .concat([
          {
            system: WearableDevice.Identifier.JunctionUser,
            value: data.junctionUserId,
          },
        ]);
    }

    const newMetadata = { ...existingDevice.metadata };
    if (data.providerLogo) {
      newMetadata.logo = data.providerLogo;
    }
    if (data.hardwareVersion) {
      newMetadata.hardwareVersion = data.hardwareVersion;
    }
    if (data.softwareVersion) {
      newMetadata.softwareVersion = data.softwareVersion;
    }
    newMetadata.updatedAt = new Date().toISOString();
    patch.note = [{ text: JSON.stringify(newMetadata) }];

    return patch;
  }

  //-----------------------------------------------------------------------------------------------
  //                                      STATIC METHODS
  //-----------------------------------------------------------------------------------------------

  static async create(
    medplum: MedplumClient,
    data: WearableDeviceData,
    options?: CreateResourceOptions,
  ): Promise<WearableDevice> {
    const created = await WearableDevice.createResource(
      medplum,
      WearableDevice.toFhirCreateObject(data),
      options,
    );
    return new WearableDevice(created);
  }

  static async createFromJunctionConnection(
    medplum: MedplumClient,
    params: {
      patientId: string;
      junctionUserId: string;
      source: JunctionSource;
      hardwareVersion?: string;
      softwareVersion?: string;
    },
  ): Promise<WearableDevice> {
    return WearableDevice.create(medplum, {
      providerSlug: getProviderSlug(params.source),
      patientId: params.patientId,
      junctionUserId: params.junctionUserId,
      providerName: params.source.name || undefined,
      providerLogo: params.source.logo || undefined,
      hardwareVersion: params.hardwareVersion,
      softwareVersion: params.softwareVersion,
      status: "active",
    });
  }

  static async searchByProviderAndPatient(
    medplum: MedplumClient,
    providerSlug: string,
    patientId: string,
  ): Promise<WearableDevice | undefined> {
    const device = await WearableDevice.searchOneResource(
      medplum,
      WearableDevice.toFhirQuery({ providerSlug, patientId }),
    );

    return device ? new WearableDevice(device) : undefined;
  }

  static async searchByJunctionUserAndProvider(
    medplum: MedplumClient,
    junctionUserId: string,
    providerSlug: string,
  ): Promise<WearableDevice | undefined> {
    const devices = await WearableDevice.searchResources(
      medplum,
      WearableDevice.toFhirQuery({ junctionUserId }),
    );

    return devices
      .map((device) => new WearableDevice(device))
      .find((device) => device.providerSlug === providerSlug);
  }

  static async searchByPatient(
    medplum: MedplumClient,
    patientId: string,
  ): Promise<WearableDevice[]> {
    const devices = await WearableDevice.searchResources(medplum, {
      ...WearableDevice.toFhirQuery({ patientId }),
      _count: 100,
    });

    return devices.map((device) => new WearableDevice(device));
  }

  static async searchActiveByPatient(
    medplum: MedplumClient,
    patientId: string,
  ): Promise<WearableDevice[]> {
    const devices = await WearableDevice.searchResources(medplum, {
      ...WearableDevice.toFhirQuery({ patientId, status: "active" }),
      _count: 100,
    });

    return devices.map((device) => new WearableDevice(device));
  }

  static async upsert(medplum: MedplumClient, data: WearableDeviceData): Promise<WearableDevice> {
    const resource = await WearableDevice.createResourceIfNoneExist(medplum, {
      resource: WearableDevice.toFhirCreateObject(data),
      ifNoneExistQuery: buildWearableDeviceConditionalCreateQuery({
        providerSlug: data.providerSlug,
        patientId: data.patientId,
      }),
    });
    const device = new WearableDevice(resource);

    if (!doesWearableDeviceNeedUpdate({ device, data })) {
      return device;
    }

    return device.updateDevice(medplum, {
      junctionUserId: data.junctionUserId,
      status: data.status,
      providerName: data.providerName,
      providerLogo: data.providerLogo,
      hardwareVersion: data.hardwareVersion,
      softwareVersion: data.softwareVersion,
    });
  }

  static async getById(medplum: MedplumClient, id: string): Promise<WearableDevice> {
    const resource = await WearableDevice.searchOneResource(medplum, { _id: id });
    if (!resource) {
      throw new Error(`WearableDevice not found: ${id}`);
    }

    return new WearableDevice(resource);
  }

  static async update(
    medplum: MedplumClient,
    { id, data, existingDevice }: WearableDeviceUpdateParams,
  ): Promise<WearableDevice> {
    const device = existingDevice ?? (await WearableDevice.getById(medplum, id));
    const patch = WearableDevice.toFhirUpdateObject(device, data);
    const resource = await WearableDevice.patchResource(medplum, { id, patch });
    return new WearableDevice(resource);
  }

  //-----------------------------------------------------------------------------------------------
  //                                     INSTANCE METHODS
  //-----------------------------------------------------------------------------------------------

  async updateDevice(
    medplum: MedplumClient,
    data: WearableDeviceUpdateData,
  ): Promise<WearableDevice> {
    return WearableDevice.update(medplum, { id: this.id, data, existingDevice: this });
  }

  async markInactive(medplum: MedplumClient): Promise<WearableDevice> {
    return this.updateDevice(medplum, { status: "inactive" });
  }

  async markActive(medplum: MedplumClient): Promise<WearableDevice> {
    return this.updateDevice(medplum, { status: "active" });
  }

  toJSON(): WearableDeviceData & { id: string } {
    return {
      id: this.id,
      providerSlug: this.providerSlug,
      patientId: this.patientId,
      junctionUserId: this.junctionUserId,
      providerName: this.providerName,
      providerLogo: this.providerLogo,
      status: this.isActive ? "active" : "inactive",
      hardwareVersion: this.hardwareVersion,
      softwareVersion: this.softwareVersion,
    };
  }
}

/** Token search on the provider slug when present, else on the Junction user id. */
function buildIdentifierQuery(query: WearableDeviceQuery): string | undefined {
  if (query.providerSlug) return `${WearableDevice.Identifier.Provider}|${query.providerSlug}`;
  if (query.junctionUserId) {
    return `${WearableDevice.Identifier.JunctionUser}|${query.junctionUserId}`;
  }
  return undefined;
}

function parseDeviceMetadata(noteText: string | undefined): Record<string, unknown> {
  if (!noteText) {
    return {};
  }

  try {
    return JSON.parse(noteText) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function buildWearableDeviceConditionalCreateQuery({
  providerSlug,
  patientId,
}: {
  providerSlug: string;
  patientId: string;
}): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(
    WearableDevice.toFhirQuery({ providerSlug, patientId }),
  )) {
    if (value === undefined) {
      continue;
    }

    params.set(key, String(value));
  }

  return params.toString();
}

function doesWearableDeviceNeedUpdate({
  device,
  data,
}: {
  device: WearableDevice;
  data: WearableDeviceData;
}): boolean {
  if (device.junctionUserId !== data.junctionUserId) {
    return true;
  }

  if (data.status !== undefined && device.isActive !== (data.status === "active")) {
    return true;
  }

  if (data.providerName !== undefined && device.providerName !== data.providerName) {
    return true;
  }

  if (data.providerLogo !== undefined && device.providerLogo !== data.providerLogo) {
    return true;
  }

  if (data.hardwareVersion !== undefined && device.hardwareVersion !== data.hardwareVersion) {
    return true;
  }

  if (data.softwareVersion !== undefined && device.softwareVersion !== data.softwareVersion) {
    return true;
  }

  return false;
}
